import { motion } from "framer-motion";
import { Star } from "lucide-react";

const REVIEWS = [
  {
    quote: "Locked my keys in the car at The Forks in -30°. Arrow had me back inside in under 25 minutes. Absolute lifesavers.",
    name: "Marcy D.", area: "St. Vital", rating: 5,
  },
  {
    quote: "Programmed a brand new fob for my F-150 right in my driveway. Half what the dealership quoted me, and zero hassle.",
    name: "Jason P.", area: "Transcona", rating: 5,
  },
  {
    quote: "Snapped my key off in my front door at 1 in the morning. They answered on the second ring and had a new key cut by 2.",
    name: "Aisha K.", area: "Downtown", rating: 5,
  },
  {
    quote: "Called three locksmiths, Arrow was the only one who picked up at 6am on a Sunday. Civic unlocked in ten minutes flat.",
    name: "Ryan M.", area: "St. James", rating: 5,
  },
  {
    quote: "Moved into our place in Bridgwater and had every lock rekeyed the same afternoon. Fair price, super tidy work.",
    name: "Priya S.", area: "Bridgwater", rating: 5,
  },
  {
    quote: "Lost my only key to the RAV4 out at Birds Hill. They cut and programmed a new one right in the parking lot. Still can't believe that was possible on-site.",
    name: "Tom W.", area: "East Kildonan", rating: 5,
  },
  {
    quote: "Key fob died the night before a road trip. Programmed a new one in my driveway by 8pm. Saved the whole trip.",
    name: "Danielle F.", area: "River Heights", rating: 5,
  },
  {
    quote: "Locked myself out of the house in slippers in January. The tech was kind, fast, and didn't make me feel dumb about it. 10/10.",
    name: "Heather B.", area: "Fort Rouge", rating: 5,
  },
  {
    quote: "Dealer quoted me $480 for a smart key. Arrow did it curbside for way less and it works perfectly.",
    name: "Josh K.", area: "Sage Creek", rating: 5,
  },
  {
    quote: "My teenager locked the keys in the trunk at Polo Park. The tech explained everything as he worked and was great with my kid's nerves.",
    name: "Monica L.", area: "Tuxedo", rating: 5,
  },
  {
    quote: "Snapped my house key off in the deadbolt on Christmas Eve. Yes, they came. Yes, on Christmas Eve.",
    name: "Gary H.", area: "North Kildonan", rating: 5,
  },
  {
    quote: "Quick, honest, and the price quoted on the phone was the price at the door. Only reason for four stars is a blizzard pushed my appointment a day — but they communicated the whole time and came first thing next morning.",
    name: "Sam R.", area: "Osborne Village", rating: 4,
  },
  {
    quote: "Got a spare made for my Wrangler before a trip up north. Took fifteen minutes in my driveway.",
    name: "Chris D.", area: "Charleswood", rating: 5,
  },
  {
    quote: "Our rental property needed three locks changed between tenants. In and out in under an hour, keys labelled and all.",
    name: "Farah N.", area: "Fort Garry", rating: 5,
  },
  {
    quote: "Frozen lock on my Silverado at 5:30 in the morning before work. He had it open and even freed up the mechanism so it wouldn't stick again.",
    name: "Dale P.", area: "Transcona", rating: 5,
  },
  {
    quote: "Bought a used Corolla that came with only one key. They cut and coded two spares for less than I expected to pay for one.",
    name: "Emily T.", area: "St. Boniface", rating: 5,
  },
  {
    quote: "Locked out with groceries melting in July. Eighteen minutes from my call to an open door. Yes, I timed it.",
    name: "Kevin J.", area: "Waverley West", rating: 5,
  },
  {
    quote: "Professional van, proper invoice, no funny business. Used them again for my mother's condo a month later.",
    name: "Sandra O.", area: "Downtown", rating: 5,
  },
  {
    quote: "Keyless entry stopped recognizing my fob. Reprogrammed on the spot, no parts needed, no upsell attempt. Refreshing.",
    name: "Amit V.", area: "St. Vital", rating: 5,
  },
  {
    quote: "They answered at 2am when my daughter locked her keys in her Jetta near the university. As a dad, that's worth every penny.",
    name: "Brian C.", area: "Fort Garry", rating: 5,
  },
  {
    quote: "Rekeyed our whole main floor after a lost purse. Same-day service, and the new keys turn smoother than the originals ever did.",
    name: "Nicole G.", area: "River Heights", rating: 5,
  },
  {
    quote: "Push-to-start wouldn't detect my key. Turned out to be a dying fob battery — they swapped it and cut me a backup blade instead of selling me a whole new fob.",
    name: "Tyler B.", area: "St. James", rating: 5,
  },
  {
    quote: "Second time using Arrow — once for my truck, once for the house. Both times fast, fair and friendly.",
    name: "Lisa M.", area: "West Kildonan", rating: 5,
  },
];

const Stars = ({ rating }) => (
  <div className="flex gap-1 mb-4" aria-label={`${rating} out of 5 stars`}>
    {Array.from({ length: 5 }).map((_, s) => (
      <Star
        key={s}
        className={`h-4 w-4 ${s < rating ? "fill-brand text-brand" : "fill-zinc-200 text-zinc-200"}`}
      />
    ))}
  </div>
);

export const Reviews = () => (
  <section id="reviews" data-testid="reviews-section" className="py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ type: "spring", stiffness: 100, damping: 20 }}
        className="mb-14 md:mb-20 flex flex-wrap items-end justify-between gap-6"
      >
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400 mb-4">Word on the street</p>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight text-zinc-950 max-w-2xl">
            Winnipeg keeps our number saved.
          </h2>
        </div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400" data-testid="reviews-summary">
          4.9 average — {REVIEWS.length} recent jobs
        </p>
      </motion.div>

      <div className="columns-1 md:columns-2 lg:columns-3 gap-6 md:gap-8">
        {REVIEWS.map((r, i) => (
          <motion.figure
            key={r.name + i}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ type: "spring", stiffness: 100, damping: 20, delay: (i % 3) * 0.08 }}
            data-testid={`review-card-${i}`}
            className="mb-6 md:mb-8 break-inside-avoid rounded-3xl bg-white border border-zinc-200 p-7 hover:-translate-y-1 transition-transform duration-300"
          >
            <Stars rating={r.rating} />
            <blockquote className="text-sm md:text-base leading-relaxed text-zinc-700">“{r.quote}”</blockquote>
            <figcaption className="mt-6 flex items-baseline gap-3">
              <span className="font-heading font-semibold text-zinc-950">{r.name}</span>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">{r.area}</span>
            </figcaption>
          </motion.figure>
        ))}
      </div>
    </div>
  </section>
);
